/**
 * Landing surface taxonomy — disambiguates demand-side pages that share the
 * same `$pathname` but behave like distinct landings.
 *
 * PostHog autocapture stores `$pathname` (query stripped) on every event,
 * so any insight filtering on path alone treats `/renta-mini-bodegas`
 * (organic SEO landing — picks city via IP/picker) the same as
 * `/renta-mini-bodegas?city=monterrey` (paid Monterrey deeplink — search
 * shell preloaded with Monterrey listings) the same as
 * `/renta-mini-bodegas/monterrey` (canonical SEO route). Those have very
 * different conversion shapes and need to be sliced independently.
 *
 * `resolveLandingSurface()` returns a single sticky slug that rides on
 * every PostHog event as the `landing_surface` super-property — set by
 * `<UTMs />` on initial mount and every SPA route change.
 *
 * Taxonomy
 * --------
 *  home                           — `/`
 *
 *  Pillars (storage / parking / warehouse / industrial)
 *  Pattern: `pillar_${pillar}_${variant}`
 *    `pillar_${p}_bare`           — `/{pillar}` (no `?city=`)
 *    `pillar_${p}_city_param`     — `/{pillar}?city=*` (paid deeplink)
 *    `pillar_${p}_city_route`     — `/{pillar}/{city}`
 *    `pillar_${p}_hood_route`     — `/{pillar}/{city}/{hood}`
 *    `pillar_${p}_listing`        — `/{pillar}/{city}/{hood}/{configId}[/*]`
 *  pillar names: `storage`, `parking`, `warehouse`, `industrial`
 *
 *  Sub-pillars (precedence: matched BEFORE the parent pillar)
 *    `ondemand_root`              — `/renta-mini-bodegas/almacenamiento-a-domicilio`
 *    `ondemand_city`              — `/renta-mini-bodegas/almacenamiento-a-domicilio/{city}`
 *    `flotillas`                  — `/pension-estacionamiento/flotillas` (B2B)
 *    `logistics`                  — `/soluciones-logisticas` (3PL demand landing)
 *
 *  Host (supply side)
 *    `host_root`                  — `/anfitrion`
 *    `host_business`              — `/anfitrion/negocio`
 *    `host_traditional`           — `/anfitrion/tradicional`
 *    `host_broker`                — `/anfitrion/broker`
 *    `host_calculate`             — `/anfitrion/valua-tu-propiedad`
 *    `host_logistics`             — `/anfitrion/soluciones-logisticas` (3PL supply)
 *    `host_invite`                — `/anfitrion/invita`
 *    `host_blog`                  — `/anfitrion/blog/*`
 *    `host_other`                 — any other `/anfitrion/*`
 *
 *  Misc
 *    `search`                     — `/search`, `/begin-search`
 *    `blog`                       — `/blog/*` or any nested `/{pillar}/blog/*`
 *    `help`                       — `/acerca-de-spotme/*`
 *    `info`                       — institutional (nosotros, medios, contacto, etc.)
 *    `directory`                  — `/directory/*`
 *    `lodging_int`                — `/alojamientos/*` (international directory)
 *    `listing_short`              — `/c/{nanoId}` (short URL, redirects to listing)
 *    `listing_lodging`            — `/alojamiento/{id}`
 *    `post`                       — `/post/*`
 *    `flow`                       — checkout / KYC / add-listing / auth flows
 *    `dashboard`                  — `/dashboard/*`, `/business/*`, `/checkoutDetails/*`
 *    `other_public`               — any other public path
 *    `unknown`                    — fallback (should be rare; logged)
 *
 * Test in DevTools:  posthog.get_property('landing_surface')
 */

const PILLAR_FROM_FIRST: Record<string, string> = {
  'renta-mini-bodegas': 'storage',
  'pension-estacionamiento': 'parking',
  'renta-bodegas-comerciales': 'warehouse',
  'renta-naves-industriales': 'industrial'
}

const HOST_PAGE_FROM_SECOND: Record<string, string> = {
  negocio: 'host_business',
  tradicional: 'host_traditional',
  broker: 'host_broker',
  'valua-tu-propiedad': 'host_calculate',
  'soluciones-logisticas': 'host_logistics',
  desarrolladores: 'host_developers',
  invita: 'host_invite'
}

const FLOW_PREFIXES = new Set([
  'checkout',
  'userConfirmation',
  'complete-profile',
  'authenticate',
  'login',
  'confirm-email'
])

const DASHBOARD_PREFIXES = new Set([
  'dashboard',
  'business',
  'checkoutDetails',
  'user'
])

const INFO_PAGES = new Set([
  'nosotros',
  'medios',
  'contacto',
  'guia-de-tamanos',
  'terminos-y-condiciones',
  'aviso-de-privacidad',
  'sitemap'
])

const stripQueryAndHash = (asPath: string): string => {
  return asPath.split('?')[0].split('#')[0]
}

const hasCityQueryParam = (asPath: string): boolean => {
  const qIndex = asPath.indexOf('?')
  if (qIndex === -1) return false
  const search = asPath.slice(qIndex + 1).split('#')[0]
  if (!search) return false
  return new URLSearchParams(search).has('city')
}

export type LandingSurface = string

/**
 * Resolve the surface slug for a Next.js asPath. Pure function — no React,
 * no DOM. Safe to call SSR-side (server resolver can use it for parity).
 */
export const resolveLandingSurface = (asPath: string): LandingSurface => {
  if (!asPath) return 'unknown'

  const path = stripQueryAndHash(asPath)
  if (path === '/' || path === '') return 'home'

  const segments = path.split('/').filter(Boolean)
  const first = segments[0]
  if (!first) return 'home'

  if (FLOW_PREFIXES.has(first)) return 'flow'
  if (DASHBOARD_PREFIXES.has(first)) return 'dashboard'
  if (first === 'api') return 'other_public'

  if (first === 'c' && segments.length >= 2) return 'listing_short'
  if (first === 'alojamiento') return 'listing_lodging'
  if (first === 'alojamientos') return 'lodging_int'
  if (first === 'directory') return 'directory'
  if (first === 'post') return 'post'

  if (first === 'search' || first === 'begin-search') return 'search'

  // Soluciones Logísticas (3PL) demand landing — standalone, not a rental
  // pillar (mirrors the ondemand_* sub-pillar pattern). Without this branch
  // 3PL sessions fell through to `other_public` and were unsliceable.
  if (first === 'soluciones-logisticas') return 'logistics'

  if (first === 'blog') return 'blog'

  if (first === 'acerca-de-spotme') return 'help'

  if (first === 'anfitrion') {
    if (segments.length === 1) return 'host_root'
    if (segments[1] === 'blog') return 'host_blog'
    const named = HOST_PAGE_FROM_SECOND[segments[1]]
    return named ?? 'host_other'
  }

  if (INFO_PAGES.has(first)) return 'info'

  // Rental pillars + sub-pillars + variants
  const pillar = PILLAR_FROM_FIRST[first]
  if (pillar) {
    // Nested blog under a pillar
    if (segments[1] === 'blog') return 'blog'

    // Almacenamiento a Domicilio (sub-pillar of storage)
    if (
      first === 'renta-mini-bodegas' &&
      segments[1] === 'almacenamiento-a-domicilio'
    ) {
      if (segments.length === 2) return 'ondemand_root'
      if (segments.length === 3) return 'ondemand_city'
      return 'ondemand_root' // deeper paths shouldn't exist; bucket safely
    }

    // Flotillas (sub-pillar of parking)
    if (
      first === 'pension-estacionamiento' &&
      segments[1] === 'flotillas' &&
      segments.length === 2
    ) {
      return 'flotillas'
    }

    if (segments.length === 1) {
      return hasCityQueryParam(asPath)
        ? `pillar_${pillar}_city_param`
        : `pillar_${pillar}_bare`
    }
    if (segments.length === 2) return `pillar_${pillar}_city_route`
    if (segments.length === 3) return `pillar_${pillar}_hood_route`
    // 4+ segments = listing detail (configId)
    return `pillar_${pillar}_listing`
  }

  return 'other_public'
}
